import { useEffect, useRef, useState } from "react";
import {
  ASSET_KIND_LABELS,
  ASSET_KINDS,
  assetTypeInfo,
  LIBRARY_UNIT_LABELS,
  LIBRARY_UNITS,
  type AssetDetail,
  type AssetDto,
  type AssetKind,
  type AssetType,
  type BuildingDto,
  type LibraryUnit,
  suggestAssetType,
} from "@klantpunt/shared";
import { ConfirmDelete, Modal } from "../../app/Modal";
import { api, ApiError } from "../../lib/api";
import { can, hasModule, useMe } from "../../lib/auth";
import { uploadPhoto } from "../../lib/photos";
import { useToast } from "../../lib/toast";
import { useBuildingCache } from "../building/useBuilding";
import { ElementPicker } from "../elements/ElementPicker";
import { AssetTypePicker } from "./AssetTypePicker";
import { assetMeterKey, meterBody, meterDraft, MeterFields, useAssetMeter } from "../meters/MeterSection";
import { useQueryClient } from "@tanstack/react-query";

const errMsg = (e: unknown) => (e instanceof ApiError ? e.message : "Er ging iets mis");

type Draft = {
  name: string;
  /** Assettype (docs/SYMBOLEN.md): symbool; vult een leeg element in. */
  assetType: AssetType | null;
  elementCode: string | null;
  floorId: string;
  roomId: string;
  yearBuilt: string;
  brand: string;
  model: string;
  serialNumber: string;
  capacity: string;
  warrantyUntil: string;
  notes: string;
  assetKind: AssetKind;
  quantity: string;
  unit: LibraryUnit | "";
  /** Hoofdasset ("" = zelfstandig). */
  parentAssetId: string;
};

/** Asset toevoegen of bewerken: categorie, naam, basisgegevens, plek. */
export function AssetDialog({
  building,
  asset,
  initial,
  onClose,
  onSaved,
}: {
  building: BuildingDto;
  asset?: AssetDto;
  /** Nieuwe asset: vooraf ingevuld (bv. na een tik op de tekening). */
  initial?: {
    floorId?: string | null;
    roomId?: string | null;
    x?: number;
    y?: number;
    elementCode?: string | null;
    assetKind?: AssetKind;
    /** Nieuw onderdeel van deze hoofdasset. */
    parentAssetId?: string;
  };
  onClose: () => void;
  onSaved?: (a: AssetDto) => void;
}) {
  const { data: me } = useMe();
  const cache = useBuildingCache(building.id);
  const toast = useToast();
  const [d, setD] = useState<Draft>({
    name: asset?.name ?? "",
    assetType: asset?.assetType ?? null,
    elementCode: asset?.elementCode ?? initial?.elementCode ?? null,
    floorId: asset?.floorId ?? initial?.floorId ?? "",
    roomId: asset?.roomId ?? initial?.roomId ?? "",
    yearBuilt: asset?.yearBuilt ? String(asset.yearBuilt) : "",
    brand: asset?.brand ?? "",
    model: asset?.model ?? "",
    serialNumber: asset?.serialNumber ?? "",
    capacity: asset?.capacity ?? "",
    warrantyUntil: asset?.warrantyUntil ?? "",
    notes: asset?.notes ?? "",
    assetKind: asset?.assetKind ?? initial?.assetKind ?? "installation",
    quantity: asset?.quantity != null ? String(asset.quantity).replace(".", ",") : "",
    unit: asset?.unit ?? "",
    parentAssetId: asset?.parentAssetId ?? initial?.parentAssetId ?? "",
  });
  const [busy, setBusy] = useState(false);
  const qc = useQueryClient();
  /** Meter: soort en telwerken in hetzelfde venster, bij een nieuwe meter én bij bewerken. */
  const [meter, setMeter] = useState(() => meterDraft(null));
  const canMeter = d.assetKind === "meter" && hasModule(me, "meters", "edit");
  const existing = useAssetMeter(asset?.id ?? "", Boolean(asset && canMeter));
  const [meterLoaded, setMeterLoaded] = useState(!asset);
  useEffect(() => {
    if (meterLoaded || !existing.data) return;
    setMeter(meterDraft(existing.data.meter));
    setMeterLoaded(true);
  }, [existing.data, meterLoaded]);
  // Bij bewerken pas tonen (en opslaan) als de huidige instellingen er zijn; anders overschrijf je ze.
  const withMeter = canMeter && meterLoaded;
  /** Nieuwe asset: foto's die na het aanmaken worden geüpload (eerste = hoofdfoto). */
  const [photos, setPhotos] = useState<{ file: File; url: string }[]>([]);
  const photoInput = useRef<HTMLInputElement>(null);
  const photosRef = useRef(photos);
  photosRef.current = photos;
  // Voorbeelden pas opruimen als het venster sluit.
  useEffect(() => () => photosRef.current.forEach((p) => URL.revokeObjectURL(p.url)), []);
  const rooms = building.rooms.filter((r) => r.floorId === d.floorId);
  // Onderdelen (docs/ASSETS-MJOP.md, Onderdelen): één niveau, dus alleen zelfstandige assets als hoofdasset,
  // en een asset met eigen onderdelen kan zelf geen onderdeel worden.
  const hasParts = !!asset && building.assets.some((x) => x.parentAssetId === asset.id);
  const parents = building.assets.filter((x) => !x.parentAssetId && x.id !== asset?.id);
  const parent = building.assets.find((x) => x.id === d.parentAssetId);
  const relink = !!asset && (asset.parentAssetId ?? "") !== d.parentAssetId;
  const set = (k: keyof Draft) => (e: { target: { value: string } }) => setD({ ...d, [k]: e.target.value });
  const year = d.yearBuilt.trim() ? Number(d.yearBuilt) : null;
  const yearOk = year === null || (Number.isInteger(year) && year >= 1800 && year <= 2100);
  const qty = d.quantity.trim() ? Number(d.quantity.trim().replace(",", ".")) : null;
  const qtyOk = qty === null || (Number.isFinite(qty) && qty >= 0);

  async function save() {
    setBusy(true);
    const body = {
      name: d.name,
      assetType: d.assetKind === "meter" ? undefined : d.assetType,
      elementCode: d.elementCode,
      floorId: d.floorId || null,
      roomId: d.floorId ? d.roomId || null : null,
      yearBuilt: year,
      brand: d.brand,
      model: d.model,
      serialNumber: d.serialNumber,
      capacity: d.capacity,
      warrantyUntil: d.warrantyUntil || null,
      notes: d.notes,
      assetKind: d.assetKind,
      quantity: qty,
      unit: d.unit || null,
      ...(!asset || relink ? { parentAssetId: d.parentAssetId || null } : {}),
      ...(!asset && initial?.x !== undefined && d.floorId === (initial.floorId ?? "")
        ? { x: initial.x, y: initial.y }
        : {}),
    };
    try {
      let x: AssetDto = asset
        ? await api<AssetDetail>(`/assets/${asset.id}`, { method: "PATCH", body })
        : await api<AssetDto>(`/buildings/${building.id}/assets`, {
            method: "POST",
            body: { id: crypto.randomUUID(), ...body },
          });
      for (const p of photos) {
        const fileId = await uploadPhoto(p.file);
        x = await api<AssetDetail>(`/assets/${x.id}/files`, { method: "POST", body: { fileId, kind: "photo" } });
      }
      if (withMeter) {
        try {
          qc.setQueryData(
            assetMeterKey(x.id),
            await api(`/assets/${x.id}/meter`, { method: "PUT", body: meterBody(meter) }),
          );
          void qc.invalidateQueries({ queryKey: ["meter-buildings"] });
          void qc.invalidateQueries({ queryKey: ["building-meters"] });
        } catch (e) {
          // De asset staat er; de meter stel je dan in het paneel in.
          toast(`Meter niet ingesteld: ${errMsg(e)}`);
        }
      }
      cache.upsertAsset(x);
      toast(asset ? "Asset opgeslagen" : `${x.code} ${x.name} toegevoegd`);
      onSaved?.(x);
      onClose();
    } catch (e) {
      toast(errMsg(e));
      setBusy(false);
    }
  }
  async function remove() {
    if (!asset) return;
    setBusy(true);
    try {
      await api(`/assets/${asset.id}`, { method: "DELETE" });
      cache.removeAsset(asset.id);
      toast(`${asset.code} “${asset.name}” verwijderd`);
      onClose();
    } catch (e) {
      toast(errMsg(e));
      setBusy(false);
    }
  }

  return (
    <Modal
      title={asset ? `${asset.code} bewerken` : parent ? `Nieuw onderdeel van ${parent.code}` : "Nieuwe asset"}
      onClose={onClose}
      busy={busy}
      wide
      footer={
        <>
          <span className="grow" />
          <button type="button" className="btn ghost" onClick={onClose}>
            Annuleren
          </button>
          <button type="button" className="btn primary" onClick={save} disabled={busy || !d.name.trim() || !yearOk}>
            Opslaan
          </button>
        </>
      }
    >
      {d.assetKind !== "meter" && (
        <>
          <p className="label">Wat is het?</p>
          <AssetTypePicker
            value={d.assetType}
            onChange={(assetType) => {
              const t = assetTypeInfo(assetType);
              // Het type vult een leeg element en een lege naam in; wat er al staat blijft staan.
              setD({
                ...d,
                assetType,
                elementCode: d.elementCode ?? t?.element ?? null,
                name: d.name.trim() ? d.name : (t?.label ?? ""),
              });
            }}
            suggestion={d.assetType ? null : suggestAssetType(d.name, d.elementCode)}
            defaultOpen={!asset && !d.assetType && !d.elementCode}
          />
        </>
      )}
      <p className="label">Element (NLBE-SfB)</p>
      <ElementPicker
        value={d.elementCode}
        onChange={(elementCode) => setD({ ...d, elementCode })}
        full={me?.org?.elementCoding ?? false}
        defaultOpen={false}
        suggestions={(() => {
          // Organisaties met de volledige tabel: de diepere codes van het type als voorstel.
          const t = assetTypeInfo(d.assetType);
          return t && me?.org?.elementCoding ? t.codes.filter((c) => c.length === 5).slice(0, 3) : [];
        })()}
      />
      <label className="field">
        <span className="label">Naam *</span>
        <input value={d.name} onChange={set("name")} placeholder="bijv. CV-ketel technische ruimte" />
      </label>
      {!asset && (
        <div className="q">
          <span className="ql">Foto's</span>
          <div className="photos">
            {photos.map((p, k) => (
              <div className="ph phedit" key={p.url}>
                <img src={p.url} alt={`Nieuwe foto ${k + 1}`} />
                <button
                  type="button"
                  className="phdel"
                  aria-label={`Foto ${k + 1} weghalen`}
                  onClick={() => {
                    URL.revokeObjectURL(p.url);
                    setPhotos(photos.filter((x) => x !== p));
                  }}
                >
                  ×
                </button>
              </div>
            ))}
            <button type="button" className="add" onClick={() => photoInput.current?.click()}>
              +<br />
              Foto
            </button>
          </div>
          <span className="hint">De eerste foto wordt de herkenningsfoto.</span>
          <input
            ref={photoInput}
            type="file"
            accept="image/*"
            multiple
            hidden
            data-testid="asset-dialog-photo-input"
            onChange={(e) => {
              const list = Array.from(e.target.files ?? []);
              setPhotos((cur) => [...cur, ...list.map((file) => ({ file, url: URL.createObjectURL(file) }))]);
              e.target.value = "";
            }}
          />
        </div>
      )}
      <p className="label">Gegevens</p>
      <div className="grid2">
        <label className="field">
          <span>Soort</span>
          <select value={d.assetKind} onChange={(e) => setD({ ...d, assetKind: e.target.value as AssetKind })}>
            {ASSET_KINDS.filter((k) => k !== "meter" || hasModule(me, "meters") || d.assetKind === "meter").map((k) => (
              <option key={k} value={k}>
                {ASSET_KIND_LABELS[k]}
              </option>
            ))}
          </select>
        </label>
        {d.assetKind !== "meter" && (
          <div className="row">
            <label className="field grow">
              <span>Hoeveelheid</span>
              <input
                inputMode="decimal"
                value={d.quantity}
                onChange={set("quantity")}
                placeholder="bijv. 640"
                aria-invalid={!qtyOk}
              />
            </label>
            <label className="field">
              <span>Eenheid</span>
              <select value={d.unit} onChange={(e) => setD({ ...d, unit: e.target.value as LibraryUnit | "" })}>
                <option value="">—</option>
                {LIBRARY_UNITS.map((u) => (
                  <option key={u} value={u}>
                    {LIBRARY_UNIT_LABELS[u]}
                  </option>
                ))}
              </select>
            </label>
          </div>
        )}
        <label className="field">
          <span>Merk</span>
          <input value={d.brand} onChange={set("brand")} />
        </label>
        <label className="field">
          <span>Type</span>
          <input value={d.model} onChange={set("model")} />
        </label>
        <label className="field">
          <span>Serienummer</span>
          <input value={d.serialNumber} onChange={set("serialNumber")} />
        </label>
        <label className="field">
          <span>Capaciteit</span>
          <input value={d.capacity} onChange={set("capacity")} placeholder="bijv. 65 kW, 630 kg, 400 l" />
        </label>
        <label className="field">
          <span>Bouwjaar</span>
          <input
            inputMode="numeric"
            value={d.yearBuilt}
            onChange={set("yearBuilt")}
            placeholder="bijv. 2018"
            aria-invalid={!yearOk}
          />
        </label>
        <label className="field">
          <span>Eind garantie</span>
          <input type="date" value={d.warrantyUntil} onChange={set("warrantyUntil")} />
        </label>
      </div>
      {withMeter && (
        <>
          <p className="label">Meter</p>
          <MeterFields
            draft={meter}
            onChange={setMeter}
            candidates={building.assets.filter((a) => a.assetKind === "meter" && a.id !== asset?.id)}
            hasKids={(existing.data?.children.length ?? 0) > 0}
          />
        </>
      )}
      <label className="field">
        <span>Notities</span>
        <textarea rows={2} value={d.notes} onChange={set("notes")} />
      </label>
      {!hasParts && (parents.length > 0 || d.parentAssetId) && (
        <label className="field">
          <span>Onderdeel van</span>
          <select
            value={d.parentAssetId}
            onChange={(e) => setD({ ...d, parentAssetId: e.target.value })}
            data-testid="asset-parent"
          >
            <option value="">— Zelfstandige asset —</option>
            {parents.map((x) => (
              <option key={x.id} value={x.id}>
                {x.code} {x.name}
              </option>
            ))}
          </select>
          <span className="hint">
            {relink
              ? d.parentAssetId
                ? `De code wordt ${parent?.code ?? ""}.n. De QR-code op een geprint label blijft werken, de code erop niet.`
                : "Deze asset krijgt een eigen nummer. De QR-code op een geprint label blijft werken, de code erop niet."
              : "Bijvoorbeeld een pomp of alarmklep van een sprinklerinstallatie. Taken en keuringen blijven bij de hoofdasset, tenzij je ze hier zelf toevoegt."}
          </span>
        </label>
      )}
      <p className="label">Waar staat deze asset?</p>
      <div className="grid2">
        <label className="field">
          <span>Verdieping</span>
          <select value={d.floorId} onChange={(e) => setD({ ...d, floorId: e.target.value, roomId: "" })}>
            <option value="">— Geen vaste plek —</option>
            {building.floors.map((f) => (
              <option key={f.id} value={f.id}>
                {f.name}
              </option>
            ))}
          </select>
        </label>
        <label className="field">
          <span>Ruimte</span>
          <select value={d.roomId} onChange={set("roomId")} disabled={!d.floorId}>
            <option value="">—</option>
            {rooms.map((r) => (
              <option key={r.id} value={r.id}>
                {r.name}
              </option>
            ))}
          </select>
        </label>
      </div>
      {asset && can(me, "asset.delete") && (
        <>
          <p className="label">Verwijderen</p>
          <ConfirmDelete
            label="Asset verwijderen"
            question={`${asset.code} “${asset.name}” verwijderen?`}
            busy={busy}
            onConfirm={remove}
            detail="Bevindingen blijven bestaan, zonder deze asset. Dat komt in hun logboek. Bijlagen worden losgekoppeld."
          />
        </>
      )}
    </Modal>
  );
}
